import { DateTime } from '@ksyos/date-time';
import type { ActivityType, GgzProfession, ServiceCode } from '@common/enums';
import type { CompensationPayoutForServiceActivity } from '@common/validation/compensationPayoutForServiceActivity';
import type { ICompensationPayout } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { dateIsAfterMaxDate, dateIsBeforeMinDate } from '@common/utils/dates';
import { doesPayoutDependOnProfession } from '@common/utils/doesPayoutDependOnProfession';
import type { CompensationPayoutAddEdit } from './ServiceActivityCompensationPayout';

export interface ICompensationPayoutWithErrors {
    amount: number | null;
    endDate: string | null;
    errors: ValidationErrors;
    id?: string;
    profession?: GgzProfession | null;
    startDate: string | null;
    type: ActivityType;
}

export function validateDateBoundary(
    compensation: ICompensationPayoutWithErrors,
    minDate?: string | Date,
    maxDate?: string | Date
) {
    let hasErrors = false;
    if (dateIsBeforeMinDate(compensation.startDate, minDate)) {
        hasErrors = true;
        compensation.errors = compensation.errors.set('startDate', 'date_beforeStartServiceRight');
    }
    if (dateIsBeforeMinDate(compensation.endDate, minDate)) {
        hasErrors = true;
        compensation.errors = compensation.errors.set('endDate', 'date_beforeStartServiceRight');
    }
    if (dateIsAfterMaxDate(compensation.startDate, maxDate)) {
        hasErrors = true;
        compensation.errors = compensation.errors.set('startDate', 'date_afterEndServiceRight');
    }
    if (dateIsAfterMaxDate(compensation.endDate, maxDate)) {
        hasErrors = true;
        compensation.errors = compensation.errors.set('endDate', 'date_afterEndServiceRight');
    }

    return { errors: compensation.errors, hasErrors };
}

export function isConfirmationRequired(
    updatedCompensationPayouts: ICompensationPayoutWithErrors[] | CompensationPayoutAddEdit[],
    oldCompensationPayouts: ICompensationPayout[] | CompensationPayoutForServiceActivity[]
) {
    const now = DateTime.now();

    for (const updatedCompensation of updatedCompensationPayouts) {
        const previousCompensation = oldCompensationPayouts.find(
            (oldCompensation) => oldCompensation.id === updatedCompensation.id
        );
        if (previousCompensation) {
            if (updatedCompensation.endDate && DateTime.from(updatedCompensation.endDate).isBefore(now)) {
                // updating a compensation record (e.g price change, start date, end date, etc) with an end date in the past
                return true;
            } else if (
                updatedCompensation.startDate &&
                DateTime.from(updatedCompensation.startDate).isBefore(previousCompensation.startDate) &&
                DateTime.from(updatedCompensation.startDate).isBefore(now)
            ) {
                // updating a compensation record with a start date in the past to an earlier date
                return true;
            }
        } else if (
            !previousCompensation &&
            updatedCompensation.startDate &&
            DateTime.from(updatedCompensation.startDate).isBefore(now)
        ) {
            // adding a compensation record with a start date in the past
            return true;
        }
    }

    for (const oldCompensation of oldCompensationPayouts) {
        const updatedCompensation = updatedCompensationPayouts.find(
            (newCompensation) => newCompensation.id === oldCompensation.id
        );
        if (!updatedCompensation && DateTime.from(oldCompensation.startDate).isBefore(now)) {
            // deleting a compensation record with a start date in the past
            return true;
        }
    }

    return false;
}

export function showPayoutOnProfessionAlert(
    organizationLevelPayoutsStartDate: string | undefined,
    serviceCode: ServiceCode | undefined,
    activityType: ActivityType | null
): boolean {
    return (
        !!organizationLevelPayoutsStartDate && !!serviceCode && doesPayoutDependOnProfession(serviceCode, activityType)
    );
}
